import React, { useCallback, useContext, useEffect, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Checkbox,
  ListItemText,
  FormHelperText,
  Box,
  Container,
  Typography,
  LinearProgress,
  Stack
} from "@mui/material";
import { CountryCode } from "libphonenumber-js";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { isValid } from "date-fns";
import {
  EditEmployeeFormData,
  EditEmployeeRequest,
  EditEmployeeResponse,
  Employee,
  EmployeeHireStatus,
  EmployeeResignReasons,
  EmploymentDuration,
  EmploymentPosition,
  EmploymentPositionLevel,
  EmploymentType,
  GetEmployeeInfoResponse,
  PermissionRole
} from "../../Graphql/types/employee";
import { isValidPhoneNumberForCountry } from "../../Utils/phoneValidatorUtil";
import { RwandaLocationParser } from "../../LocationsParser/RwandaLocationParser";
import { formatMuiFormDate, getEighteenYearsAgo } from "./utils/dateUtils";
import { getEditEmployeeRequest } from "./utils/formUtils";
import {
  getEmployeeByIdQuery,
  updateEmployeeQuery
} from "../../Graphql/querries/employeeQuery";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { EMPLOYEE_PORTAL } from "../Routes";
import WithAuth from "../../Hoc/WithAuth";

const countryCode = process.env.REACT_APP_COUNTRY_CODE || "RW";
const urlRegex =
  /https?:\/\/(www\.)?[a-zA-Z0-9-._]+(\.[a-zA-Z]{2,})+(\/\S*)?\.(png|jpg|jpeg)$/i;

const EditEmployee: React.FC = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const countries = ["RWANDA"];
  const [locationData, setLocationData] = useState({});
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [cells, setCells] = useState<string[]>([]);
  const [villages, setVillages] = useState<string[]>([]);
  const [employeeDetail, setEmployeeDetail] = useState<Employee | undefined>();
  // Extract the 'id' from the URL parameters
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();

  // Initialize the instance when component loads and keep it unchanged
  const getRwandaLocationParser = useCallback(
    () => new RwandaLocationParser(),
    []
  );

  // initialize location data
  useEffect(() => {
    getRwandaLocationParser()
      .getLocationData()
      .then((data) => setLocationData(data));
  }, []);

  // Validation Schema
  const schema: yup.ObjectSchema<EditEmployeeFormData> = yup.object().shape({
    firstName: yup
      .string()
      .required("Firstname is required")
      .min(2, "Firstname must be at least 2 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    lastName: yup
      .string()
      .required("Lastname is required")
      .min(2, "Lastname must be at least 3 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    avatar: yup
      .string()
      .required("Avatar is required")
      .matches(urlRegex, "Avatar must be a valid URL"),
    phoneNumber: yup
      .string()
      .required("PhoneNumber is required")
      .test("is-valid-phone", "Please enter a valid phone number", (value) =>
        value
          ? isValidPhoneNumberForCountry(value, countryCode as CountryCode)
          : false
      ),
    dateOfBirth: yup
      .date()
      .typeError("Invalid date format")
      .max(getEighteenYearsAgo(), "Employee must be at least 18 years old")
      .required("Date of birth is required"),
    positionLevel: yup
      .string()
      .oneOf(
        Object.values(EmploymentPositionLevel),
        "Invalid Position lvel selected"
      )
      .required("Position level is required")
      .min(2, "Position level must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    employmentType: yup
      .string()
      .oneOf(Object.values(EmploymentType), "Invalid Employment Type selected")
      .required("Employment Type is required")
      .min(2, "Employment Type must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    role: yup
      .string()
      .oneOf(Object.values(PermissionRole), "Invalid Job role selected")
      .required("Job role is required")
      .min(2, "Job role must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    employmentDuration: yup
      .string()
      .oneOf(Object.values(EmploymentDuration), "Invalid Duration selected")
      .required("Duration is required")
      .min(2, "Duration must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    hireStatus: yup
      .string()
      .oneOf(Object.values(EmployeeHireStatus), "Invalid status selected")
      .required("Hire status is required")
      .min(2, "Hire status must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    positions: yup
      .array()
      .of(
        yup
          .string()
          .oneOf(Object.values(EmploymentPosition), "Invalid position selected")
          .required()
      )
      .min(1, "Select at least one position")
      .required("Positions are required"),

    country: yup
      .string()
      .oneOf(Object.values(countries), "Invalid country selected")
      .required("Country is required")
      .min(2, "Country must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    province: yup
      .string()
      .oneOf(Object.values(provinces), "Invalid province selected")
      .required("Province is required")
      .min(2, "Province must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    district: yup
      .string()
      .oneOf(Object.values(districts), "Invalid district selected")
      .required("District is required")
      .min(2, "District must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    sector: yup
      .string()
      .oneOf(Object.values(sectors), "Invalid sector selected")
      .required("Sector is required")
      .min(2, "Sector must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    cell: yup
      .string()
      .oneOf(Object.values(cells), "Invalid cell selected")
      .required("Cell is required")
      .min(2, "Cell must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    village: yup
      .string()
      .oneOf(Object.values(villages), "Invalid village selected")
      .required("Village is required")
      .min(2, "Village must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    hireDate: yup
      .date()
      .typeError("Invalid date format")
      .max(new Date(), "Date must be in the past")
      .required("Hire date is required"),

    resignDate: yup.lazy((date) => {
      if (date) {
        return yup
          .date()
          .typeError("Invalid date format")
          .max(new Date(), "Date must be in the past")
          .required("Resign Date must be valid if provided");
      }
      return yup.date().optional();
    }),
    rehireDate: yup.lazy((date) => {
      if (date && isValid(date)) {
        return yup
          .date()
          .typeError("Invalid date format")
          .max(new Date(), "Date must be in the past")
          .required("Date is required");
      }
      return yup.date().optional();
    }),
    resignReason: yup.lazy((resignReason) => {
      if (resignReason) {
        return yup
          .mixed<EmployeeResignReasons>()
          .oneOf(
            Object.values(EmployeeResignReasons),
            "Invalid resign reason selected"
          )
          .required("Resign reason is required");
      }

      return yup
        .mixed<EmployeeResignReasons>()
        .oneOf(Object.values(EmployeeResignReasons), "Invalid reason selected")
        .nullable()
        .optional();
    })
  });

  const { hiredEmployeeInfo } = employeeDetail || {};
  const { address } = hiredEmployeeInfo || {};

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<EditEmployeeFormData>({
    resolver: yupResolver(schema)
  });

  const selectedProvince =
    useWatch({ control, name: "province" }) || address?.province || "";
  const selectedDistrict =
    useWatch({ control, name: "district" }) || address?.district || "";
  const selectedSector =
    useWatch({ control, name: "sector" }) || address?.sector || "";
  const selectedCell =
    useWatch({ control, name: "cell" }) || address?.cell || "";

  // get provinces
  useEffect(() => {
    (async () => {
      setProvinces(getRwandaLocationParser().getProvinces(locationData));
    })();
  }, [locationData]);

  // Get districts based on provinces
  useEffect(() => {
    if (selectedProvince) {
      setDistricts(
        getRwandaLocationParser().getDistrictByProvince(
          selectedProvince,
          locationData
        )
      );
    }
  }, [selectedProvince, address?.province]);

  // Get sectors based on districts
  useEffect(() => {
    if (selectedDistrict) {
      setSectors(
        getRwandaLocationParser().getSectors(
          selectedProvince,
          selectedDistrict,
          locationData
        )
      );
    }
  }, [selectedDistrict]);

  // Get cells based on sectors
  useEffect(() => {
    if (selectedSector) {
      setCells(
        getRwandaLocationParser().getCells(
          selectedProvince,
          selectedDistrict,
          selectedSector,
          locationData
        )
      );
    }
  }, [selectedSector]);

  // Get villages based on villages
  useEffect(() => {
    if (selectedCell) {
      setVillages(
        getRwandaLocationParser().getVillages(
          selectedProvince,
          selectedDistrict,
          selectedSector,
          selectedCell,
          locationData
        )
      );
    }
  }, [selectedCell]);

  const { mutate, isPending } = useMutation<
    EditEmployeeResponse,
    Error,
    EditEmployeeRequest
  >({
    mutationKey: ["(updateEmployee"],
    mutationFn: (input) =>
      fetchGraphQL(updateEmployeeQuery, { input }, setAlert),
    onSuccess: (response) => {
      // navigate to employee detail upon creation
      navigate(`${EMPLOYEE_PORTAL}/${response.adminUpdateEmployee.id}`);
    }
  });

  const { mutate: getEmployeeDetails, isPending: fetchingEmpDetail } =
    useMutation<GetEmployeeInfoResponse, Error, string>({
      mutationKey: ["getEmployeeDetail"],
      mutationFn: (id) => fetchGraphQL(getEmployeeByIdQuery, { id }, setAlert),
      onSuccess: (response) => {
        const data = response.getEmployeeById;
        setEmployeeDetail(data);
      }
    });

  useEffect(() => {
    if (employeeId) getEmployeeDetails(employeeId);
  }, []);

  const handleClickSubmit = (data: EditEmployeeFormData) => {
    if (employeeId)
      mutate(
        getEditEmployeeRequest(
          data,
          employeeId,
          !!hiredEmployeeInfo?.isVerified
        )
      );
  };

  if (fetchingEmpDetail) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  if (!employeeDetail) {
    return <Typography>Employee doesn&apos;t exist</Typography>;
  }

  return (
    <Stack
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        width: "100%",
        padding: "1em"
      }}
    >
      <Typography variant="h5" textAlign="center">
        Edit Employee
      </Typography>
      <Stack
        style={{
          display: "flex",
          justifyContent: "start",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
      </Stack>
      <Container
        maxWidth="md"
        className="jobFormContainer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          flexDirection: "column",
          boxSizing: "border-box",
          padding: "0 2em",
          gap: 2
        }}
      >
        <Box
          component="form"
          noValidate
          autoComplete="on"
          style={{ width: "100%" }}
          display="flex"
          flexDirection="column"
          gap={1.7}
          onSubmit={handleSubmit(handleClickSubmit)}
        >
          {/* Text Fields */}
          {["firstName", "lastName", "avatar", "phoneNumber"].map(
            (fieldItem) => (
              <Controller
                key={fieldItem}
                name={fieldItem as unknown as keyof EditEmployeeFormData}
                control={control}
                defaultValue={
                  fieldItem === "avatar"
                    ? hiredEmployeeInfo?.[fieldItem] || ""
                    : employeeDetail[fieldItem] || ""
                }
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors[fieldItem]}
                    size="medium"
                    id="Job Title"
                    label={`Enter ${fieldItem}`}
                    variant="outlined"
                    helperText={errors?.[fieldItem]?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            )
          )}

          {/* Date Fields */}
          {["dateOfBirth", "hireDate"].map((fieldItem) => (
            <Controller
              key={fieldItem}
              name={fieldItem as keyof EditEmployeeFormData}
              control={control}
              defaultValue={formatMuiFormDate(hiredEmployeeInfo?.[fieldItem])}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Select ${fieldItem}`}
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors[fieldItem]}
                  helperText={errors?.[fieldItem]?.message}
                  fullWidth
                  margin="normal"
                  style={{ width: "100%!important" }}
                  size="medium"
                />
              )}
            />
          ))}

          {/* Multi-Select Positions */}
          <Controller
            name="positions"
            control={control}
            defaultValue={hiredEmployeeInfo?.positions || []}
            render={({ field }) => (
              <FormControl fullWidth margin="normal">
                <InputLabel id="positions-label" shrink>
                  Positions
                </InputLabel>
                <Select
                  multiple
                  {...field}
                  renderValue={(selected) => (selected as string[]).join(", ")}
                  error={!!errors.positions}
                  size="medium"
                  labelId="positions-label"
                  name="positions"
                >
                  {Object.values(EmploymentPosition).map((position) => (
                    <MenuItem key={position} value={position}>
                      <Checkbox checked={field?.value?.includes(position)} />
                      <ListItemText primary={position} />
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.positions && (
                  <FormHelperText sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}>
                    {errors.positions.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Dropdowns */}
          {[
            {
              name: "positionLevel",
              values: Object.values(EmploymentPositionLevel)
            },
            { name: "employmentType", values: Object.values(EmploymentType) },
            {
              name: "employmentDuration",
              values: Object.values(EmploymentDuration)
            },
            { name: "role", values: Object.values(PermissionRole) },
            { name: "hireStatus", values: Object.values(EmployeeHireStatus) },
            {
              name: "resignReason",
              values: Object.values(EmployeeResignReasons)
            }
          ].map(({ name, values }) => (
            <Controller
              key={name}
              name={name as keyof EditEmployeeFormData}
              control={control}
              defaultValue={
                name === "role"
                  ? employeeDetail[name]
                  : hiredEmployeeInfo?.[name]
              }
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel>{name}</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ""}
                    error={!!errors[name as keyof EditEmployeeFormData]}
                  >
                    {values.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors[name as keyof EditEmployeeFormData] && (
                    <FormHelperText error>
                      {errors[name as keyof EditEmployeeFormData]?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          ))}

          {[
            { name: "country", values: countries },
            { name: "province", values: provinces },
            { name: "district", values: districts },
            { name: "sector", values: sectors },
            { name: "cell", values: cells },
            { name: "village", values: villages }
          ].map(({ name, values }) => (
            <Controller
              key={name}
              name={name as keyof EditEmployeeFormData}
              control={control}
              defaultValue={address?.[name] || ""}
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel>{name}</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ""}
                    error={!!errors[name as keyof EditEmployeeFormData]}
                  >
                    {values.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors[name as keyof EditEmployeeFormData] && (
                    <FormHelperText error>
                      {errors[name as keyof EditEmployeeFormData]?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          ))}

          {["rehireDate", "resignDate"].map((fieldItem) => (
            <Controller
              key={fieldItem}
              name={fieldItem as keyof EditEmployeeFormData}
              control={control}
              defaultValue={formatMuiFormDate(hiredEmployeeInfo?.[fieldItem])}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Select ${fieldItem}`}
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors[fieldItem]}
                  helperText={errors?.[fieldItem]?.message}
                  fullWidth
                  margin="normal"
                  style={{ width: "100%!important" }}
                  size="medium"
                />
              )}
            />
          ))}

          {isPending && (
            <Stack width="100%" paddingBottom="1em" alignSelf="center">
              <LinearProgress />
            </Stack>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            style={{
              marginTop: "1em",
              width: "40%",
              padding: "0.8em",
              margin: "auto"
            }}
          >
            Edit Employee
          </Button>
        </Box>
      </Container>
    </Stack>
  );
};

export default WithAuth(EditEmployee);
